import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AppState } from './types';
import { createProfileSlice } from './slices/profile-slice';
import { createPlanSlice } from './slices/plan-slice';
import { createAthleteSlice } from './slices/athlete-slice';
import { createUISlice } from './slices/ui-slice';
import { createGamificationSlice } from './slices/gamification-slice';
import { createFeaturesSlice } from './slices/features-slice';
import { createDiscoverySlice } from './slices/discovery-slice';
import { createSubscriptionSlice } from './slices/subscription-slice';
import { initialState } from './constants';
import { sessionStateStorage } from './storage';
import {
    normalizePreferences,
    normalizeFeatureFlags,
    normalizeSubscription,
    normalizeReferrals,
} from './normalizers';

// Session-scoped local cache:
// Supabase remains the persistent source of truth for cross-session data.
export const useStore = create<AppState>()(
    persist(
        (set, get, api) => ({
            ...createProfileSlice(set, get, api),
            ...createPlanSlice(set, get, api),
            ...createAthleteSlice(set, get, api),
            ...createUISlice(set, get, api),
            ...createGamificationSlice(set, get, api),
            ...createFeaturesSlice(set, get, api),
            ...createDiscoverySlice(set, get, api),
            ...createSubscriptionSlice(set, get, api),

            reset: () => set(initialState as Partial<AppState>),
        }),
        {
            name: 'adaptive-endurance-storage-v1',
            storage: createJSONStorage(() => sessionStateStorage),
            version: 2,
            migrate: (persistedState) => {
                const persisted = (persistedState ?? {}) as Partial<AppState>;
                return {
                    ...persisted,
                    preferences: normalizePreferences(persisted.preferences),
                    featureFlags: normalizeFeatureFlags(persisted.featureFlags),
                    subscription: normalizeSubscription(persisted.subscription),
                    referrals: normalizeReferrals(persisted.referrals),
                    plan: Array.isArray(persisted.plan) ? persisted.plan : initialState.plan,
                    blocks: Array.isArray(persisted.blocks) ? persisted.blocks : initialState.blocks,
                    events: Array.isArray(persisted.events) ? persisted.events : initialState.events,
                } as AppState;
            },
            merge: (persistedState, currentState) => {
                const persisted = (persistedState ?? {}) as Partial<AppState>;
                // Defense-in-depth against stale auth in legacy storage. Older
                // builds persisted authUser, and `sessionStateStorage` migrates
                // legacy IndexedDB/localStorage payloads as-is. If we let that
                // flow through, the merge spreads a stale authUser onto the
                // fresh store, PublicRoute redirects "/" -> "/dashboard", and
                // ProtectedRoute then bounces to "/onboarding" before Supabase
                // has had a chance to declare the session invalid. Always drop
                // any persisted authUser so Supabase remains the only source
                // of truth for auth.
                const { authUser: _legacyAuthUser, ...persistedSafe } = persisted;
                void _legacyAuthUser;
                return {
                    ...currentState,
                    ...persistedSafe,
                    preferences: normalizePreferences(persisted.preferences),
                    featureFlags: normalizeFeatureFlags(persisted.featureFlags),
                    subscription: normalizeSubscription(persisted.subscription),
                    referrals: normalizeReferrals(persisted.referrals),
                    plan: Array.isArray(persisted.plan) ? persisted.plan : currentState.plan,
                    blocks: Array.isArray(persisted.blocks) ? persisted.blocks : currentState.blocks,
                    events: Array.isArray(persisted.events) ? persisted.events : currentState.events,
                };
            },
            partialize: (state) => {
                // Defense-in-depth: also check if the active profile is a coached
                // athlete, even if isImpersonating was not set (e.g. stale state).
                const activeProfile = state.profiles.find(p => p.id === state.activeProfileId);
                const isTrueImpersonation = (
                    (state.isImpersonating && Boolean(state.impersonatedUserId) && state.impersonatedUserId !== state.authUser?.id)
                    || (activeProfile?.relation === 'coached' && state.activeProfileId !== state.authUser?.id)
                );

                // SAFETY: If impersonating, DO NOT persist the impersonated data.
                // This prevents the "Zombie State" bug where a coach reloads the page
                // and accidentally overwrites their own profile with the athlete's data
                // because cached local state rehydrated the athlete's data while the auth session
                // reverted to the coach.
                if (isTrueImpersonation) {
                    return {
                        userRole: state.userRole,
                        profiles: state.profiles,
                        // Revert active profile to self/default to ensure clean slate on reload
                        activeProfileId: state.profiles.find(p => p.relation === 'self')?.id || state.profiles[0]?.id || null,
                        preferences: state.preferences,
                        consent: state.consent,
                        stats: state.stats,
                        config: state.config,
                        enabledSports: state.enabledSports,
                        discovery: state.discovery,
                        // Exclude: athlete, metrics, goal, events, intervals, availability, plan, blocks
                    } as unknown as AppState;
                }

                return {
                    userRole: state.userRole,
                    profiles: state.profiles,
                    activeProfileId: state.activeProfileId,
                    preferences: state.preferences,
                    consent: state.consent,
                    stats: state.stats,
                    athlete: state.athlete,
                    metrics: state.metrics,
                    goal: state.goal,
                    events: state.events,
                    intervals: state.intervals,
                    availability: state.availability,
                    longRunDay: state.longRunDay,
                    plan: state.plan,
                    planState: state.planState,
                    planName: state.planName,
                    planHistory: state.planHistory,
                    blocks: state.blocks,
                    config: state.config,
                    progression: state.progression,
                    cyclingProgression: state.cyclingProgression,
                    planSettings: state.planSettings,
                    gamification: state.gamification,
                    // Feature flags and triathlon
                    enabledSports: state.enabledSports,
                    triathlonProgression: state.triathlonProgression,
                    discovery: state.discovery,
                    subscription: state.subscription,
                    featureFlags: state.featureFlags,
                    referrals: state.referrals,
                    // authUser is intentionally NOT persisted. Supabase owns the
                    // session lifecycle; caching it locally lets a stale id
                    // outlive a logged-out session and trigger redirect loops
                    // (PublicRoute -> /dashboard -> ProtectedRoute -> /onboarding).
                };
            },
        }
    )
);

// SELECTORS
export const selectAthlete = (state: AppState) => state.athlete;
export const selectMetrics = (state: AppState) => state.metrics;
export const selectGoal = (state: AppState) => state.goal;
export const selectPlan = (state: AppState) => state.plan;
export const selectBlocks = (state: AppState) => state.blocks;
export const selectPlanConfig = (state: AppState) => state.config;
export const selectAvailability = (state: AppState) => state.availability;
export const selectProgression = (state: AppState) => state.progression;
export const selectIsLoading = (state: AppState) => state.isLoading;

export const selectWeek = (weekNum: number) => (state: AppState) =>
    state.plan?.find(w => w.weekNumber === weekNum);

export const selectTotalVolume = (state: AppState) =>
    (state.plan ?? []).reduce((sum, week) => sum + (week.actualVolume || week.targetVolume), 0);

export const selectPlanState = (state: AppState) => state.planState;
export const selectPlanName = (state: AppState) => state.planName;
export const selectPlanHistory = (state: AppState) => state.planHistory;

export const selectProfiles = (state: AppState) => state.profiles;
export const selectActiveProfileId = (state: AppState) => state.activeProfileId;
export const selectUserRole = (state: AppState) => state.userRole;
export const selectActiveProfile = (state: AppState) =>
    state.profiles.find(p => p.id === state.activeProfileId);
export const selectIsCoach = (state: AppState) =>
    state.userRole === 'coach' || state.profiles.some(p => p.relation === 'coached');

// Feature flags selectors
export const selectEnabledSports = (state: AppState) => state.enabledSports;
export const selectIsTriathlonEnabled = (state: AppState) => state.enabledSports.triathlon;
export const selectIsSwimmingEnabled = (state: AppState) => state.enabledSports.swimming;

// Triathlon selectors
export const selectTriathlonProgression = (state: AppState) => state.triathlonProgression;
export const selectIsTriathlonAthlete = (state: AppState) => state.athlete?.sport === 'Triathlon';
